import { isAccDetailId } from './fiuu-topup.js';

/**
 * Wallet top-up by e-wallet / DuitNow QR through Revenue Monster (MYP-135).
 *
 * The flow, from the vendor's guide "Revenue Monster 充值接口对接文档（RM sandbox）" (2026-11-03):
 *
 * 1. The app calls `rm_pay` with the amount. The vendor records an `accdetail` as INVALID (not paid
 *    yet), orders the payment from RM, and answers with `accdetailid`, `rechargeno` and `payurl` —
 *    RM's own checkout page, with no gateway page of the vendor's in between (unlike FPX).
 * 2. The browser opens `payurl`; the customer pays there.
 * 3. RM tells the vendor (`rm_notify`); the vendor asks RM to confirm, then turns the `accdetail`
 *    into END and credits the wallet, once.
 * 4. RM sends the browser back through the vendor (`rm_redirect`), which sends it on to
 *    `/wallet/top-up/result?orderid=…&accdetailid=…&status=…` — the same result page as FPX, read
 *    the same way (`findFiuuTopUpState`, `toTopUpReturnHint`).
 *
 * Only steps 1 and 2 are new, and they are here: no React, no network, so they can be tested.
 * `code` is not read here: `legacy-api/envelope.ts` already turns every refusal into a failed result.
 */

/** The vendor's answer when an e-wallet top-up is started: where to send the customer to pay. */
export interface RmTopUpStart {
  /** The top-up's own record id — what the result page looks it up by. */
  readonly accdetailid: string;
  /** The top-up number (`rechargeno`), when there is one: the `orderid` the result page is sent. */
  readonly rechargeno?: string;
  readonly amountCents: number;
  /** RM's checkout page. Always passed `isSafeRmPayUrl` before it gets here. */
  readonly payUrl: string;
}

/**
 * The hosts RM's checkout page may be on — the only places the app sends a customer to pay by
 * e-wallet. **Sandbox only** for now (MYP-135: the new site has no production RM merchant yet), so
 * a production link is refused until its host is added here.
 *
 * - `sb-pg.revenuemonster.my`: RM's own documentation of Hosted Payment Checkout gives the page as
 *   `https://sb-pg.revenuemonster.my/v4/checkout?checkoutId=…`.
 * - `sb-open.revenuemonster.my`: the vendor's guide shows `payurl` as
 *   `https://sb-open.revenuemonster.my/...` (RM's sandbox API host). Kept until the first sandbox
 *   top-up shows which one the vendor really sends; then drop the other.
 */
export const RM_CHECKOUT_HOSTS: readonly string[] = [
  'sb-pg.revenuemonster.my',
  'sb-open.revenuemonster.my',
];

type Payload = Readonly<Record<string, unknown>>;

function asPayload(value: unknown): Payload | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Payload)
    : undefined;
}

function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * `"100.00"`, `"100.5"` or `100` → whole cents; undefined for anything but a positive amount with
 * at most two decimals. The same reading as `fiuu-topup.ts`: from the digits, never through a float.
 */
function positiveCents(value: unknown): number | undefined {
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(text(value) ?? '');
  if (match === null) return undefined;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return cents > 0 ? cents : undefined;
}

/**
 * Whether a link may be opened as RM's checkout page: `https`, the normal port, no user or
 * password, and exactly one of `RM_CHECKOUT_HOSTS`. Read with the `URL` parser, not by matching
 * text, which links like `https://sb-pg.revenuemonster.my@evil.example/` fool. The path and query
 * are RM's to choose (the checkout id), so they are not checked — only that there is a path.
 */
export function isSafeRmPayUrl(url: unknown): boolean {
  if (typeof url !== 'string') return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return (
    parsed.protocol === 'https:' &&
    parsed.username === '' &&
    parsed.password === '' &&
    parsed.port === '' && // `:443` reads as '' too; any other port is refused
    RM_CHECKOUT_HOSTS.includes(parsed.hostname) &&
    parsed.pathname !== '/'
  );
}

/**
 * `rm_pay`'s answer (the whole body; its fields sit under `data`) → where to send the customer.
 * Undefined when it cannot be used: no `data`, no `accdetailid`, no usable amount, a link that is
 * not RM's checkout page, or an amount other than the one the customer asked for
 * (`requestedCents`). The vendor answers the amount in ringgit, as a number (`100`).
 */
export function normaliseRmTopUpStart(
  body: unknown,
  requestedCents: number,
): RmTopUpStart | undefined {
  const payload = asPayload(asPayload(body)?.['data']);
  if (payload === undefined) return undefined;

  const accdetailid = text(payload['accdetailid']);
  const amountCents = positiveCents(payload['amount']);
  const payUrl = text(payload['payurl']);
  if (!isAccDetailId(accdetailid)) return undefined;
  if (amountCents === undefined || payUrl === undefined) return undefined;
  if (!isSafeRmPayUrl(payUrl)) return undefined;
  if (amountCents !== requestedCents) return undefined;

  return { accdetailid, rechargeno: text(payload['rechargeno']), amountCents, payUrl };
}
